/**
 * `notify()`: the sweetalert call shape, routed onto the app's own popups
 * (MYD-535).
 *
 * 29 files called `swal()`, which drew its own unstyled modal outside the
 * design system. Rewriting 97 calls by hand, with their `.then` chains and
 * button configs, would risk changing behaviour in screens nobody re-tested.
 * Instead each call keeps its arguments and gets the standard UI:
 *
 *   - A QUESTION, meaning a call that offers a way out (two buttons, a
 *     `cancel` button, or `dangerMode`), becomes `toast.confirm()`: the centred
 *     modal from MYD-529. It resolves the confirm button's value (normally
 *     `true`) on confirm and `null` on cancel, which is what sweetalert
 *     resolved, so every `.then(value => …)` and `await` reads the same.
 *   - Anything else is a NOTIFICATION: `toast.success / error / warning /
 *     info` by icon, resolving `true` at once. That includes the "OK"-only
 *     alerts, which only ever said something and never waited on the answer.
 *
 * New code should call `toast` directly; this exists for the migrated sites.
 */
import { toast } from './toast-store'

type SwalIcon = 'success' | 'error' | 'warning' | 'info' | string

/** A sweetalert button. Its styling keys (`className`, `closeModal`) are accepted and ignored. */
interface ButtonConfig {
	text?: string
	value?: unknown
	visible?: boolean
	[key: string]: unknown
}

export interface NotifyOptions {
	title?: string
	text?: string
	icon?: SwalIcon
	buttons?:
		| boolean
		| Array<string | boolean>
		| { cancel?: ButtonConfig | boolean; confirm?: ButtonConfig | boolean; [key: string]: unknown }
	dangerMode?: boolean
	[key: string]: unknown
}

/** Normalise the three sweetalert call shapes into one options object. */
const normalise = (a?: string | NotifyOptions, b?: string | NotifyOptions, c?: string): NotifyOptions => {
	if (typeof a === 'object' && a) return a
	const first = typeof a === 'string' ? a : undefined
	if (typeof b === 'object' && b) return { ...b, text: first } // swal(text, options)
	if (typeof b === 'string') return { title: first, text: b, icon: c } // swal(title, text, icon)
	return { text: first } // swal(text)
}

const labelOf = (button: unknown, fallback: string) =>
	typeof button === 'string' ? button : (button as ButtonConfig)?.text || fallback

/**
 * The cancel and confirm buttons, when the call is a question; otherwise null.
 */
const questionButtons = (opts: NotifyOptions) => {
	const { buttons, dangerMode } = opts
	if (Array.isArray(buttons)) {
		// swal's array form is [cancel, confirm]; a falsy cancel means no cancel.
		if (buttons.length >= 2 && buttons[0]) {
			return { cancel: labelOf(buttons[0], 'Cancel'), confirm: labelOf(buttons[1], 'OK'), value: true as unknown }
		}
		return null
	}
	if (buttons && typeof buttons === 'object') {
		if (!buttons.cancel) return null
		const confirm = buttons.confirm as ButtonConfig | boolean | undefined
		const value = confirm && typeof confirm === 'object' && 'value' in confirm ? confirm.value : true
		return { cancel: labelOf(buttons.cancel, 'Cancel'), confirm: labelOf(confirm, 'OK'), value }
	}
	if (dangerMode) return { cancel: 'Cancel', confirm: 'OK', value: true as unknown }
	return null
}

function notify(
	textOrOptions?: string | NotifyOptions,
	textOrSecond?: string | NotifyOptions,
	icon?: string
): Promise<any> {
	const opts = normalise(textOrOptions, textOrSecond, icon)
	const question = questionButtons(opts)

	if (question) {
		return toast
			.confirm({
				title: opts.title,
				message: opts.text || '',
				confirmLabel: question.confirm,
				cancelLabel: question.cancel,
				variant: opts.dangerMode ? 'destructive' : opts.icon === 'warning' ? 'warning' : 'default',
			})
			.then(confirmed => (confirmed ? question.value : null))
	}

	// A title alone reads as the message; with text, the title heads it.
	const message = opts.text || opts.title || ''
	const title = opts.text ? opts.title : undefined
	const show =
		opts.icon === 'success'
			? toast.success
			: opts.icon === 'error'
				? toast.error
				: opts.icon === 'warning'
					? toast.warning
					: toast.info
	show(message, { title })
	return Promise.resolve(true)
}

export default notify
